import { useState, useEffect } from 'react'

/**
 * Custom hook for localStorage with automatic JSON serialization
 * @param {string} key - Storage key
 * @param {*} initialValue - Initial value if nothing in storage
 * @returns {[value, setValue]} - Current value and setter function
 */
export const useLocalStorage = (key, initialValue) => {
  // Get value from localStorage or use initialValue
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  })

  // Return a wrapped version of useState's setter function that persists to localStorage
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value
      
      // Save state
      setStoredValue(valueToStore)
      
      // Save to localStorage
      if (valueToStore === undefined) {
        window.localStorage.removeItem(key)
      } else {
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error)
    }
  }

  return [storedValue, setValue]
}

/**
 * Hook for persistent user info across assessments
 * This data is NEVER cleared and persists across multiple assessments
 * 
 * STORAGE SCHEMA:
 * - cedec-persistent-user-info: User information (name, email, company, clientId, phone, consultantEmail)
 */
export const usePersistentUserInfo = () => {
  const [userInfo, setUserInfo] = useLocalStorage('cedec-persistent-user-info', null)

  /**
   * Migrate old persistent user info to include clientId field
   * This ensures backward compatibility with data saved before clientId was added
   */
  const getMigratedUserInfo = () => {
    if (!userInfo) return null
    
    // If clientId already exists, return as-is
    if ('clientId' in userInfo) {
      return userInfo
    }
    
    // Old format detected - add clientId field (empty string, user will fill it)
    console.log('🔄 Migrating persistent user info to include clientId field')
    const migratedInfo = {
      ...userInfo,
      clientId: '' // Empty string, will be filled by user on next assessment
    }
    
    // Save migrated version
    setUserInfo(migratedInfo)
    return migratedInfo
  }

  /**
   * Save user info persistently (survives across assessments)
   */
  const savePersistentUserInfo = (info) => {
    if (!info) {
      console.warn('⚠️ Cannot save null user info')
      return
    }

    // Only save the fields we want to persist (exclude assessmentDate which changes per assessment)
    const persistentFields = {
      firstName: info.firstName || '',
      lastName: info.lastName || '',
      email: info.email || '',
      company: info.company || '',
      clientId: info.clientId || '', // ✅ Include clientId - it's part of user identity
      phone: info.phone || '',
      consultantEmail: info.consultantEmail || ''
    }

    console.log('💾 Saving persistent user info for future assessments', persistentFields)
    setUserInfo(persistentFields)
  }

  /**
   * Clear persistent user info (rare case, user wants to completely reset)
   */
  const clearPersistentUserInfo = () => {
    console.log('🗑️ Clearing persistent user info')
    setUserInfo(null)
  }

  return {
    persistentUserInfo: getMigratedUserInfo(), // Return migrated version
    savePersistentUserInfo,
    clearPersistentUserInfo,
    hasPersistentUserInfo: () => userInfo !== null
  }
}

/**
 * Hook for saving assessment progress with data validation
 * 
 * STORAGE SCHEMA:
 * - cedec-client-info: Client information (name, email, company, etc.) - TEMPORARY, cleared after each assessment
 * - cedec-assessment-scores: Individual question scores (Object mapping question IDs to scores)
 * - cedec-assessment-data: Complete assessment data (themes, questionnaire, metadata) - NEW!
 * - cedec-current-step: Current step in the assessment flow
 * - cedec-current-theme-index: Current theme index (0-based)
 * - cedec-data-version: Version number for schema validation
 */

// Current data schema version - increment when making breaking changes
// 1.0.0 - Initial version
// 1.1.0 - Added URL-based navigation with query parameters for themes
const CURRENT_VERSION = '1.1.0'
const PREVIOUS_VERSION = '1.0.0'

export const useAssessmentProgress = () => {
  const [clientInfo, setClientInfo] = useLocalStorage('cedec-client-info', null)
  const [assessmentScores, setAssessmentScores] = useLocalStorage('cedec-assessment-scores', null)
  const [assessmentData, setAssessmentData] = useLocalStorage('cedec-assessment-data', null)
  const [currentStep, setCurrentStep] = useLocalStorage('cedec-current-step', 'client-info')
  const [currentThemeIndex, setCurrentThemeIndex] = useLocalStorage('cedec-current-theme-index', 0)
  const [dataVersion, setDataVersion] = useLocalStorage('cedec-data-version', CURRENT_VERSION)

  // 🛡️ Validate and migrate data from older versions
  const validateAndMigrateData = () => {
    // No version number = very old data, clear it
    if (!dataVersion) {
      console.warn('⚠️ No version number found in localStorage. Clearing old data for safety.')
      clearProgress()
      setDataVersion(CURRENT_VERSION)
      return { migrated: true, cleared: true }
    }

    // Same version = no migration needed
    if (dataVersion === CURRENT_VERSION) {
      return { migrated: false, cleared: false }
    }

    // Version 1.0.0 → 1.1.0: Non-breaking change, just update version
    if (dataVersion === PREVIOUS_VERSION) {
      console.log(`📦 Migrating from v${PREVIOUS_VERSION} to v${CURRENT_VERSION}`)
      console.log('✅ Data structure compatible, updating version number only')
      
      // Validate theme index from old version
      const savedTheme = localStorage.getItem('cedec-current-theme-index')
      if (savedTheme) {
        try {
          const themeIndex = JSON.parse(savedTheme)
          if (typeof themeIndex !== 'number' || themeIndex < 0) {
            console.warn('⚠️ Invalid theme index in old data, resetting to 0')
            setCurrentThemeIndex(0)
          }
        } catch (e) {
          console.warn('⚠️ Corrupted theme index, resetting to 0')
          setCurrentThemeIndex(0)
        }
      }
      
      setDataVersion(CURRENT_VERSION)
      return { migrated: true, cleared: false }
    }

    // Unknown version or future version = clear for safety
    console.warn(`⚠️ Unknown version (saved: ${dataVersion}, current: ${CURRENT_VERSION}). Clearing data.`)
    clearProgress()
    setDataVersion(CURRENT_VERSION)
    return { migrated: true, cleared: true }
  }

  // Validate on mount
  if (typeof window !== 'undefined') {
    const result = validateAndMigrateData()
    if (result.cleared) {
      console.log('🔄 localStorage cleared due to incompatible version')
    } else if (result.migrated) {
      console.log('✅ localStorage migrated successfully')
    }
  }

  const saveClientInfo = (info) => {
    // Validate client info structure
    if (!info || !info.email || !info.company) {
      console.warn('⚠️ Invalid client info structure', info)
      return
    }
    
    setClientInfo(info)
    setCurrentStep('assessment')
  }

  const saveAssessmentScores = (scores) => {
    // Validate scores structure
    if (!scores || typeof scores !== 'object') {
      console.warn('⚠️ Invalid scores structure', scores)
      return
    }
    
    setAssessmentScores(scores)
  }
  
  const saveCurrentThemeIndex = (index) => {
    // Validate theme index
    if (typeof index !== 'number' || index < 0 || !isFinite(index)) {
      console.warn('⚠️ Invalid theme index', index)
      return
    }
    
    // Cap at reasonable maximum (100 themes should be more than enough)
    const cappedIndex = Math.min(Math.floor(index), 100)
    
    setCurrentThemeIndex(cappedIndex)
  }
  
  /**
   * Get validated theme index - ensures it's a valid number
   */
  const getValidThemeIndex = () => {
    if (typeof currentThemeIndex !== 'number' || currentThemeIndex < 0 || !isFinite(currentThemeIndex)) {
      console.warn('⚠️ Corrupted theme index detected, returning 0')
      return 0
    }
    return Math.floor(currentThemeIndex)
  }

  /**
   * Save complete assessment data (themes, questionnaire, etc.)
   * This is needed to display the success page after refresh
   */
  const saveCompleteAssessmentData = (data) => {
    // Validate assessment data structure
    if (!data || !data.scores || !data.themes || !data.questionnaire) {
      console.warn('⚠️ Invalid assessment data structure', data)
      return
    }
    
    console.log('💾 Saving complete assessment data to localStorage')
    setAssessmentData(data)
  }

  const clearProgress = () => {
    console.log('🗑️ Clearing all assessment progress from localStorage')
    setClientInfo(null)
    setAssessmentScores(null)
    setAssessmentData(null)
    setCurrentStep('client-info')
    setCurrentThemeIndex(0) // Reset theme index
    // Keep version number
  }

  /**
   * Clear everything including version (nuclear option)
   */
  const clearAll = () => {
    console.log('💣 Nuclear clear: Removing ALL localStorage data')
    localStorage.removeItem('cedec-client-info')
    localStorage.removeItem('cedec-assessment-scores')
    localStorage.removeItem('cedec-assessment-data')
    localStorage.removeItem('cedec-current-step')
    localStorage.removeItem('cedec-current-theme-index')
    localStorage.removeItem('cedec-data-version')
    window.location.reload()
  }

  const hasProgress = () => {
    return clientInfo !== null || assessmentScores !== null
  }

  /**
   * Check if we have complete data for success page
   */
  const hasCompleteAssessmentData = () => {
    return assessmentData !== null && 
           assessmentData.scores && 
           assessmentData.themes && 
           assessmentData.questionnaire
  }

  /**
   * Get assessment data with validation
   */
  const getValidatedAssessmentData = () => {
    if (!assessmentData) {
      // This is normal for fresh starts, not a warning
      return null
    }

    // Validate structure
    if (!assessmentData.scores || !assessmentData.themes) {
      console.error('❌ Assessment data is corrupted or incomplete', assessmentData)
      return null
    }

    return assessmentData
  }

  return {
    // Data
    clientInfo,
    assessmentScores,
    assessmentData: getValidatedAssessmentData(),
    currentStep,
    currentThemeIndex: getValidThemeIndex(), // Always return validated theme index
    dataVersion,
    
    // Actions
    saveClientInfo,
    saveAssessmentScores,
    saveCurrentThemeIndex,
    saveCompleteAssessmentData,
    setCurrentStep,
    clearProgress,
    clearAll,
    
    // Validators
    hasProgress,
    hasCompleteAssessmentData,
    validateAndMigrateData,
    getValidThemeIndex
  }
}

export default useLocalStorage
